// Central de Indicações (pedido 2026-09-27) — exclusiva do app, aberta por
// Perfil → Indicações (só cliente, accountType "usuario"). Três telas
// internas: principal, Minhas indicações e Detalhes. Tudo vem do backend
// real (src/referrals/ no worktree release); nada aqui muda etapa.
import { useEffect, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Check,
  ChevronRight,
  Copy,
  Handshake,
  Link2,
  MessageCircle,
  RefreshCw,
  Share2,
  Sparkles,
  Trophy,
  UserPlus,
  Users,
  Medal,
  Star,
  Gift,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useReferralDetail, useReferralList, useReferralSummary } from "@/hooks/queries/referrals";
import {
  referralUrl,
  type ReferralAchievement,
  type ReferralStatus,
} from "@/services/gpsdrive-referrals.service";

type View = { name: "home" } | { name: "list" } | { name: "detail"; id: string };

export function ReferralsCenterScreen({ onClose }: { onClose: () => void }) {
  const [view, setView] = useState<View>({ name: "home" });
  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-50 dark:bg-slate-950">
      <ReferralStyles />
      {view.name === "home" && (
        <ReferralHome onBack={onClose} onOpenList={() => setView({ name: "list" })} />
      )}
      {view.name === "list" && (
        <ReferralList
          onBack={() => setView({ name: "home" })}
          onOpen={(id) => setView({ name: "detail", id })}
        />
      )}
      {view.name === "detail" && (
        <ReferralDetailScreen id={view.id} onBack={() => setView({ name: "list" })} />
      )}
    </div>
  );
}

// ---- visual compartilhado ---------------------------------------------------

const STATUS_META: Record<ReferralStatus, { label: string; pill: string; dot: string }> = {
  cadastrado: {
    label: "Cadastrado",
    pill: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
    dot: "bg-violet-500",
  },
  negociacao: {
    label: "Em negociação",
    pill: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    dot: "bg-amber-500",
  },
  cliente: {
    label: "Cliente fechado",
    pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
};

const ACHIEVEMENT_ICON: Record<ReferralAchievement["key"], typeof Trophy> = {
  first: Star,
  five: Medal,
  ten: Trophy,
  five_clients: BadgeCheck,
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function initials(name: string) {
  return name
    .replace(/\./g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

// Keyframes próprios (confete caindo, presente flutuando, entrada suave) —
// desligados com prefers-reduced-motion.
function ReferralStyles() {
  return (
    <style>{`
      @keyframes ref-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
      @keyframes ref-twinkle { 0%,100% { opacity: .25; transform: scale(.8) } 50% { opacity: 1; transform: scale(1.1) } }
      @keyframes ref-fall { 0% { transform: translateY(-10px) rotate(0) } 100% { transform: translateY(14px) rotate(160deg) } }
      @keyframes ref-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
      .ref-float { animation: ref-float 3.2s ease-in-out infinite }
      .ref-twinkle { animation: ref-twinkle 2.4s ease-in-out infinite }
      .ref-fall { animation: ref-fall 2.8s ease-in-out infinite alternate }
      .ref-in { animation: ref-in .35s ease-out both }
      @media (prefers-reduced-motion: reduce) {
        .ref-float, .ref-twinkle, .ref-fall, .ref-in { animation: none }
      }
    `}</style>
  );
}

function BackButton({ onClick, light }: { onClick: () => void; light?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Voltar"
      className={cn(
        "grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors",
        light
          ? "bg-white/15 text-white hover:bg-white/25"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200",
      )}
    >
      <ArrowLeft className="h-5 w-5" />
    </button>
  );
}

function PlainHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div
      className="flex items-center gap-3 bg-white px-4 pb-3 dark:bg-slate-900"
      style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
    >
      <BackButton onClick={onBack} />
      <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">{title}</h1>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center" role="alert">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-500/15">
        <RefreshCw className="h-6 w-6" />
      </div>
      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        Não foi possível carregar suas indicações
      </p>
      <p className="text-xs text-slate-500">Verifique sua conexão e tente de novo.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white active:bg-blue-700"
      >
        Tentar novamente
      </button>
    </div>
  );
}

function Shimmer({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-2xl bg-slate-200/80 dark:bg-slate-800", className)} />;
}

// Personagem + presentes + confete, desenhados em SVG (sem imagem externa).
function HeroIllustration() {
  return (
    <div className="relative h-[150px] w-[150px] shrink-0" aria-hidden>
      {/* confetes */}
      {[
        ["left-2 top-3", "bg-amber-300", "0s"],
        ["right-3 top-1", "bg-pink-400", ".6s"],
        ["left-8 top-16", "bg-sky-300", "1.1s"],
        ["right-0 top-14", "bg-emerald-300", ".3s"],
        ["left-0 top-28", "bg-violet-300", "1.5s"],
      ].map(([pos, color, delay], i) => (
        <span
          key={i}
          className={cn("ref-fall absolute h-2 w-1.5 rounded-sm", pos, color)}
          style={{ animationDelay: delay }}
        />
      ))}
      {[
        ["right-10 top-4", "0s"],
        ["left-4 top-9", ".8s"],
        ["right-2 top-24", "1.4s"],
      ].map(([pos, delay], i) => (
        <Sparkles
          key={i}
          className={cn("ref-twinkle absolute h-3.5 w-3.5 text-amber-200", pos)}
          style={{ animationDelay: delay }}
        />
      ))}
      <svg viewBox="0 0 150 150" className="absolute inset-0 h-full w-full">
        {/* sombra */}
        <ellipse cx="78" cy="142" rx="46" ry="5" fill="#000" opacity=".18" />
        {/* corpo */}
        <path d="M52 142 C52 110 60 96 78 96 C96 96 104 110 104 142 Z" fill="#3b82f6" />
        <path d="M70 97 L78 110 L86 97" fill="#fff" opacity=".9" />
        {/* braço erguido segurando o celular */}
        <path d="M98 108 C108 100 112 88 110 76" stroke="#f5c29b" strokeWidth="9" strokeLinecap="round" fill="none" />
        <rect x="101" y="56" width="18" height="28" rx="4" fill="#0f172a" />
        <rect x="103.5" y="59.5" width="13" height="20" rx="2" fill="#38bdf8" />
        <path d="M107 68 l3 3 l5 -6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
        {/* braço baixo */}
        <path d="M57 110 C50 118 50 126 54 132" stroke="#f5c29b" strokeWidth="9" strokeLinecap="round" fill="none" />
        {/* cabeça */}
        <circle cx="78" cy="76" r="17" fill="#f5c29b" />
        <path d="M61 73 C61 58 72 54 80 55 C91 56 96 63 95 73 C90 66 82 64 74 66 C68 67 64 70 61 73 Z" fill="#1e293b" />
        <circle cx="72" cy="78" r="1.8" fill="#1e293b" />
        <circle cx="84" cy="78" r="1.8" fill="#1e293b" />
        <path d="M72 85 Q78 90 84 85" stroke="#1e293b" strokeWidth="2" fill="none" strokeLinecap="round" />
        <circle cx="68" cy="84" r="2.5" fill="#fb7185" opacity=".45" />
        <circle cx="88" cy="84" r="2.5" fill="#fb7185" opacity=".45" />
      </svg>
      {/* presentes */}
      <div className="ref-float absolute bottom-1 left-0">
        <GiftBox color="#f43f5e" ribbon="#fde68a" size={40} />
      </div>
      <div className="ref-float absolute bottom-0 left-9" style={{ animationDelay: ".9s" }}>
        <GiftBox color="#f59e0b" ribbon="#fff7ed" size={28} />
      </div>
    </div>
  );
}

function GiftBox({ color, ribbon, size }: { color: string; ribbon: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
      <rect x="4" y="16" width="32" height="22" rx="3" fill={color} />
      <rect x="2" y="11" width="36" height="8" rx="2.5" fill={color} />
      <rect x="2" y="11" width="36" height="8" rx="2.5" fill="#000" opacity=".12" />
      <rect x="17" y="11" width="6" height="27" fill={ribbon} />
      <path d="M20 11 C14 2 6 6 11 11 Z M20 11 C26 2 34 6 29 11 Z" fill={ribbon} />
    </svg>
  );
}

// ---- copiar / compartilhar -------------------------------------------------

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

const shareMessage = (url: string) =>
  `Uso o GPS Drive para rastrear meu veículo e recomendo! Faça seu cadastro pelo meu link: ${url}`;

// ---- tela principal ----------------------------------------------------------

function ReferralHome({ onBack, onOpenList }: { onBack: () => void; onOpenList: () => void }) {
  const { data, isLoading, isError, refetch } = useReferralSummary();
  const url = data ? referralUrl(data.code) : "";
  const [copied, setCopied] = useState(false);
  const [shareSheet, setShareSheet] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    const ok = await copyText(url);
    setCopied(ok);
    setFeedback(ok ? "Link copiado" : "Não foi possível copiar o link");
  };

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Indicação GPS Drive", text: shareMessage(url), url });
      } catch {
        // cancelado pelo usuário
      }
      return;
    }
    setShareSheet(true);
  };

  return (
    <div className="flex-1 overflow-y-auto pb-10">
      {/* cabeçalho azul-marinho */}
      <div
        className="relative overflow-hidden rounded-b-[32px] bg-gradient-to-br from-[#081534] via-[#0f2a66] to-[#1d4ed8] px-5 pb-16 text-white"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-sky-400/10" />
        <div className="relative flex items-center gap-3">
          <BackButton onClick={onBack} light />
          <h1 className="text-lg font-bold">Indicações</h1>
        </div>
        <div className="relative mt-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide">
              <Gift className="h-3.5 w-3.5" /> Programa de indicação
            </span>
            <h2 className="mt-2 text-[24px] font-extrabold leading-tight">Indique e ganhe juntos!</h2>
            <p className="mt-1.5 text-[13px] leading-snug text-blue-100">
              Compartilhe seu link e acompanhe todas as suas indicações por aqui.
            </p>
          </div>
          <HeroIllustration />
        </div>
      </div>

      <div className="relative -mt-12 space-y-4 px-4">
        {/* link exclusivo */}
        <section className="ref-in rounded-3xl bg-white p-4 shadow-lg shadow-slate-900/5 dark:bg-slate-900">
          <div className="text-[13px] font-semibold text-slate-500 dark:text-slate-400">Seu link exclusivo</div>
          {isLoading ? (
            <Shimmer className="mt-2 h-11" />
          ) : isError ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              <div className="mt-2 flex items-center gap-2 rounded-2xl border border-dashed border-blue-200 bg-blue-50/70 px-3 py-2.5 dark:border-blue-500/30 dark:bg-blue-500/10">
                <Link2 className="h-4 w-4 shrink-0 text-blue-600" />
                <span className="truncate font-mono text-[13px] text-slate-700 dark:text-slate-200" title={url}>
                  {url.replace(/^https?:\/\//, "")}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className={cn(
                    "flex h-11 items-center justify-center gap-2 rounded-2xl text-sm font-semibold transition-colors",
                    copied
                      ? "bg-emerald-500 text-white"
                      : "bg-blue-50 text-blue-700 active:bg-blue-100 dark:bg-blue-500/15 dark:text-blue-300",
                  )}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copiado!" : "Copiar link"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex h-11 items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-semibold text-white shadow-md shadow-blue-600/25 active:bg-blue-700"
                >
                  <Share2 className="h-4 w-4" />
                  Compartilhar
                </button>
              </div>
              <p className="sr-only" aria-live="polite">
                {feedback}
              </p>
            </>
          )}
        </section>

        {/* indicadores */}
        <section aria-label="Resumo das indicações" className="ref-in grid grid-cols-2 gap-3" style={{ animationDelay: ".05s" }}>
          {(
            [
              ["Pessoas indicadas", data?.stats.total, Users, "from-blue-500 to-indigo-500"],
              ["Cadastrados", data?.stats.cadastrados, UserPlus, "from-violet-500 to-fuchsia-500"],
              ["Em negociação", data?.stats.negociacao, Handshake, "from-amber-400 to-orange-500"],
              ["Clientes fechados", data?.stats.clientes, Trophy, "from-emerald-400 to-teal-500"],
            ] as const
          ).map(([label, value, Icon, gradient]) => (
            <div key={label} className="rounded-3xl bg-white p-4 shadow-sm dark:bg-slate-900">
              <div
                className={cn(
                  "grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br text-white shadow-md",
                  gradient,
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              {isLoading ? (
                <Shimmer className="mt-3 h-7 w-12" />
              ) : (
                <div className="mt-3 text-[26px] font-extrabold leading-none text-slate-900 dark:text-slate-50">
                  {value ?? 0}
                </div>
              )}
              <div className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{label}</div>
            </div>
          ))}
        </section>

        {/* próxima conquista */}
        {data && (
          <section className="ref-in overflow-hidden rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 p-4 shadow-sm ring-1 ring-amber-100 dark:from-amber-500/10 dark:to-orange-500/10 dark:ring-amber-500/20" style={{ animationDelay: ".1s" }}>
            {data.nextAchievement ? (
              <NextAchievement a={data.nextAchievement} />
            ) : (
              <div className="flex items-center gap-3">
                <TrophyBadge big />
                <div>
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-50">Você conquistou tudo!</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    Todas as conquistas foram desbloqueadas. Obrigado por indicar!
                  </div>
                </div>
              </div>
            )}
            {data.achievements.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {data.achievements.map((a) => {
                  const Icon = ACHIEVEMENT_ICON[a.key];
                  return (
                    <span
                      key={a.key}
                      className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-700 shadow-sm dark:bg-slate-900 dark:text-amber-300"
                    >
                      <Icon className="h-3.5 w-3.5" /> {a.title}
                    </span>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* atalho */}
        <button
          type="button"
          onClick={onOpenList}
          className="ref-in flex w-full items-center gap-3 rounded-3xl bg-white p-4 text-left shadow-sm active:bg-slate-50 dark:bg-slate-900 dark:active:bg-slate-800"
          style={{ animationDelay: ".15s" }}
        >
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-600 text-white">
            <Users className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-slate-900 dark:text-slate-50">Minhas indicações</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {data ? `${data.stats.total} ${data.stats.total === 1 ? "pessoa indicada" : "pessoas indicadas"}` : "Acompanhe cada etapa"}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </button>

        <p className="px-2 text-center text-[11px] leading-relaxed text-slate-400">
          As etapas são atualizadas automaticamente conforme o atendimento de cada indicado avança.
        </p>
      </div>

      {shareSheet && <ShareSheet url={url} onClose={() => setShareSheet(false)} onCopy={handleCopy} />}
    </div>
  );
}

function TrophyBadge({ big }: { big?: boolean }) {
  return (
    <div
      className={cn(
        "grid shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-amber-300 to-orange-500 text-white shadow-md shadow-orange-500/30",
        big ? "h-12 w-12" : "h-10 w-10",
      )}
    >
      <Trophy className={big ? "h-6 w-6" : "h-5 w-5"} />
    </div>
  );
}

function NextAchievement({ a }: { a: ReferralAchievement }) {
  const pct = Math.round((a.current / a.target) * 100);
  const missing = a.target - a.current;
  const unit = a.key === "five_clients" ? (missing === 1 ? "cliente" : "clientes") : missing === 1 ? "indicação" : "indicações";
  return (
    <div className="flex items-center gap-3">
      <TrophyBadge big />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
          Próxima conquista
        </div>
        <div className="text-sm font-bold text-slate-900 dark:text-slate-50">{a.title}</div>
        <div
          className="mt-2 h-2.5 overflow-hidden rounded-full bg-white dark:bg-slate-800"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={a.target}
          aria-valuenow={a.current}
          aria-label={`Progresso: ${a.current} de ${a.target}`}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-[width] duration-700"
            style={{ width: `${Math.max(pct, 4)}%` }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-slate-600 dark:text-slate-300">
          <span>
            Falta{missing === 1 ? "" : "m"} {missing} {unit}
          </span>
          <span className="font-semibold">
            {a.current}/{a.target}
          </span>
        </div>
      </div>
    </div>
  );
}

// Opções quando o aparelho não tem compartilhamento nativo (WebView do APK).
function ShareSheet({ url, onClose, onCopy }: { url: string; onClose: () => void; onCopy: () => void }) {
  return (
    <div className="absolute inset-0 z-50 flex items-end bg-slate-900/50" onClick={onClose} role="dialog" aria-modal="true" aria-label="Compartilhar link">
      <div
        className="ref-in w-full rounded-t-3xl bg-white p-5 dark:bg-slate-900"
        style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-slate-200 dark:bg-slate-700" />
        <div className="mb-3 text-base font-bold text-slate-900 dark:text-slate-50">Compartilhar link</div>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareMessage(url))}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="flex items-center gap-3 rounded-2xl p-3 active:bg-slate-50 dark:active:bg-slate-800"
        >
          <span className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-white">
            <MessageCircle className="h-5 w-5" />
          </span>
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">WhatsApp</span>
        </a>
        <button
          type="button"
          onClick={() => {
            onCopy();
            onClose();
          }}
          className="flex w-full items-center gap-3 rounded-2xl p-3 text-left active:bg-slate-50 dark:active:bg-slate-800"
        >
          <span className="grid h-10 w-10 place-items-center rounded-full bg-blue-600 text-white">
            <Copy className="h-5 w-5" />
          </span>
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Copiar link</span>
        </button>
      </div>
    </div>
  );
}

// ---- Minhas indicações -------------------------------------------------------

const FILTERS: { label: string; value?: ReferralStatus }[] = [
  { label: "Todas" },
  { label: "Cadastrados", value: "cadastrado" },
  { label: "Negociação", value: "negociacao" },
  { label: "Clientes", value: "cliente" },
];

function ReferralList({ onBack, onOpen }: { onBack: () => void; onOpen: (id: string) => void }) {
  const [filter, setFilter] = useState<ReferralStatus | undefined>(undefined);
  const { data, isLoading, isError, refetch } = useReferralList(filter);

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-white dark:bg-slate-950">
      <PlainHeader title="Minhas indicações" onBack={onBack} />
      <div className="flex gap-2 overflow-x-auto px-4 pb-3 pt-1 [scrollbar-width:none]" role="tablist" aria-label="Filtrar indicações">
        {FILTERS.map((f) => {
          const active = f.value === filter;
          return (
            <button
              key={f.label}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(f.value)}
              className={cn(
                "shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors",
                active
                  ? "bg-blue-600 text-white shadow-md shadow-blue-600/25"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 space-y-2.5 overflow-y-auto px-4 pb-8">
        {isLoading ? (
          Array.from({ length: 4 }, (_, i) => <Shimmer key={i} className="h-[72px]" />)
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <EmptyList filtered={!!filter} onBack={onBack} />
        ) : (
          data.map((r, i) => {
            const meta = STATUS_META[r.status];
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => onOpen(r.id)}
                className="ref-in flex w-full items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3.5 text-left shadow-sm active:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:active:bg-slate-800"
                style={{ animationDelay: `${Math.min(i, 8) * 0.03}s` }}
                aria-label={`${r.name}, ${meta.label}, indicado em ${fmtDate(r.registeredAt)}`}
              >
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
                  {initials(r.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-bold text-slate-900 dark:text-slate-50">{r.name}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Indicado em {fmtDate(r.registeredAt)}</div>
                </div>
                <span className={cn("inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold", meta.pill)}>
                  <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
                  {meta.label}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function EmptyList({ filtered, onBack }: { filtered: boolean; onBack: () => void }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="relative">
        <GiftBox color="#3b82f6" ribbon="#fde68a" size={72} />
        <Sparkles className="ref-twinkle absolute -right-3 -top-2 h-5 w-5 text-amber-400" />
      </div>
      <p className="mt-4 text-base font-bold text-slate-900 dark:text-slate-50">
        {filtered ? "Nenhuma indicação nesta etapa" : "Nenhuma indicação ainda"}
      </p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {filtered
          ? "Quando alguma indicação chegar aqui, ela aparece nesta lista."
          : "Compartilhe seu link: quem se cadastrar por ele aparece aqui."}
      </p>
      {!filtered && (
        <button
          type="button"
          onClick={onBack}
          className="mt-5 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-600/25 active:bg-blue-700"
        >
          Compartilhar meu link
        </button>
      )}
    </div>
  );
}

// ---- Detalhes ---------------------------------------------------------------

const TIMELINE_META: Record<string, { icon: typeof Link2; color: string; ring: string }> = {
  link: { icon: Link2, color: "bg-sky-500", ring: "ring-sky-100 dark:ring-sky-500/20" },
  cadastro: { icon: UserPlus, color: "bg-violet-500", ring: "ring-violet-100 dark:ring-violet-500/20" },
  negociacao: { icon: Handshake, color: "bg-amber-500", ring: "ring-amber-100 dark:ring-amber-500/20" },
  cliente: { icon: BadgeCheck, color: "bg-emerald-500", ring: "ring-emerald-100 dark:ring-emerald-500/20" },
};

function ReferralDetailScreen({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, isLoading, isError, refetch } = useReferralDetail(id);
  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-white dark:bg-slate-950">
      <PlainHeader title="Detalhes da indicação" onBack={onBack} />
      <div className="flex-1 overflow-y-auto px-4 pb-10">
        {isLoading ? (
          <div className="space-y-3 pt-2">
            <Shimmer className="h-20" />
            <Shimmer className="h-64" />
          </div>
        ) : isError || !data ? (
          <ErrorState onRetry={() => refetch()} />
        ) : (
          <>
            <div className="ref-in flex items-center gap-3 rounded-3xl bg-gradient-to-br from-[#0f2a66] to-[#1d4ed8] p-4 text-white shadow-lg shadow-blue-900/20">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-white/15 text-base font-bold">
                {initials(data.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-base font-bold">{data.name}</div>
                <div className="text-xs text-blue-100">Indicado em {fmtDate(data.registeredAt)}</div>
              </div>
              <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-slate-800">
                {STATUS_META[data.status].label}
              </span>
            </div>

            {data.status === "cliente" && (
              <div className="ref-in relative mt-4 overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-400 to-teal-500 p-4 text-white shadow-md">
                {["left-4 top-2 bg-amber-200", "right-8 top-3 bg-pink-200", "right-3 bottom-3 bg-white", "left-1/2 top-1 bg-sky-200"].map((c, i) => (
                  <span key={i} className={cn("ref-fall absolute h-2 w-1.5 rounded-sm", c)} style={{ animationDelay: `${i * 0.4}s` }} />
                ))}
                <div className="relative flex items-center gap-3">
                  <span className="text-3xl" aria-hidden>🎉</span>
                  <div>
                    <div className="text-sm font-extrabold">Parabéns! {data.name.split(" ")[0]} agora é cliente</div>
                    <div className="text-xs text-emerald-50">Obrigado por indicar. Sua indicação virou um novo cliente.</div>
                  </div>
                </div>
              </div>
            )}

            <h2 className="mb-3 mt-6 text-sm font-bold text-slate-900 dark:text-slate-50">Linha do tempo</h2>
            <ol className="relative">
              {data.timeline.map((step, i) => {
                const meta = TIMELINE_META[step.key]!;
                const Icon = meta.icon;
                const done = !!step.at;
                const last = i === data.timeline.length - 1;
                const nextDone = !last && !!data.timeline[i + 1]!.at;
                return (
                  <li key={step.key} className="relative flex gap-3 pb-6 last:pb-0">
                    {!last && (
                      <span
                        className={cn(
                          "absolute left-[19px] top-10 h-[calc(100%-2.5rem)] w-0.5",
                          nextDone ? "bg-gradient-to-b from-blue-400 to-blue-300" : "border-l-2 border-dashed border-slate-200 dark:border-slate-700",
                        )}
                        aria-hidden
                      />
                    )}
                    <span
                      className={cn(
                        "relative grid h-10 w-10 shrink-0 place-items-center rounded-full ring-4",
                        done ? cn(meta.color, meta.ring, "text-white") : "bg-slate-100 text-slate-400 ring-slate-50 dark:bg-slate-800 dark:ring-slate-900",
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 pt-1.5">
                      <div className={cn("text-sm font-semibold", done ? "text-slate-900 dark:text-slate-50" : "text-slate-400")}>
                        {step.title}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {step.at ? fmtDateTime(step.at) : "Aguardando"}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {data.achievements.length > 0 && (
              <>
                <h2 className="mb-3 mt-7 text-sm font-bold text-slate-900 dark:text-slate-50">Conquistas</h2>
                <div className="grid grid-cols-2 gap-3">
                  {data.achievements.map((a) => (
                    <AchievementCard key={a.key} a={a} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AchievementCard({ a }: { a: ReferralAchievement }): ReactNode {
  const Icon = ACHIEVEMENT_ICON[a.key];
  return (
    <div
      className={cn(
        "ref-in relative overflow-hidden rounded-3xl p-3.5 shadow-sm",
        a.unlockedByThis
          ? "bg-gradient-to-br from-amber-300 to-orange-500 text-white"
          : "bg-amber-50 text-slate-900 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-slate-50 dark:ring-amber-500/20",
      )}
    >
      <div
        className={cn(
          "grid h-10 w-10 place-items-center rounded-2xl",
          a.unlockedByThis ? "bg-white/25" : "bg-gradient-to-br from-amber-300 to-orange-500 text-white",
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-2 text-sm font-bold leading-tight">{a.title}</div>
      <div className={cn("mt-0.5 text-[11px]", a.unlockedByThis ? "text-amber-50" : "text-slate-500 dark:text-slate-400")}>
        {a.unlockedByThis ? "Desbloqueada por esta indicação" : a.achievedAt ? `Desde ${fmtDate(a.achievedAt)}` : a.description}
      </div>
    </div>
  );
}
